import React, { useEffect, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import spocService from '../../services/spocService';

import AddSpocModal from '../../components/features/spocs/AddSpocModal';
import EditSpocModal from '../../components/features/spocs/EditSpocModal';

const SpocsView = () => {
  const [spocs, setSpocs] = useState([]);
  const [filteredSpocs, setFilteredSpocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSpoc, setSelectedSpoc] = useState(null);

  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadSpocs();
  }, []);

  useEffect(() => {
    filterSpocs();
  }, [searchTerm, spocs]);

  const loadSpocs = async () => {
    setLoading(true);
    const result = await spocService.getAllSpocs();
    if (result.success) {
      setSpocs(result.data);
      setFilteredSpocs(result.data);
    } else {
      error(result.error || 'Failed to load SPOCs');
    }
    setLoading(false);
  };

  const filterSpocs = () => {
    let filtered = [...spocs];

    if (searchTerm) {
      filtered = filtered.filter(spoc =>
        spoc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        spoc.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSpocs(filtered);
  };

  const handleAddSpoc = async (spocData) => {
    const result = await spocService.createSpoc(spocData);
    if (result.success) {
      success('SPOC added successfully!');
      await loadSpocs();
    } else {
      error(result.error || 'Failed to add SPOC');
    }
  };

  const handleEditSpoc = (spoc) => {
    setSelectedSpoc(spoc);
    setShowEditModal(true);
  };

  const handleUpdateSpoc = async (spocId, spocData) => {
    const result = await spocService.updateSpoc(spocId, spocData);
    if (result.success) {
      success('SPOC updated successfully!');
      await loadSpocs();
    } else {
      error(result.error || 'Failed to update SPOC');
    }
  };

  const handleDeleteSpoc = (spoc) => {
    setSelectedSpoc(spoc);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await spocService.deleteSpoc(selectedSpoc.id);
    if (result.success) {
      success('SPOC deleted successfully!');
      await loadSpocs();
    } else {
      error(result.error || 'Failed to delete SPOC');
    }
    setShowDeleteDialog(false);
    setSelectedSpoc(null);
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const columns = [
    {
      key: 'name',
      label: 'SPOC',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px'
          }}>
            {value.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {row.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact'
    },
    {
      key: 'location',
      label: 'Company / Site',
      render: (value, row) => {
        const companyName = row.company?.company_name || row.company_name || 'Unassigned';
        const siteName = row.site?.name || row.site_name || 'No Site';
        return (
          <div>
            <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{companyName}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{siteName}</div>
          </div>
        );
      }
    },
    {
      key: 'team_lead',
      label: 'Team Lead Assigned',
      render: (value) => {
        if (!value) {
          return (
            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px' }}>
              Not Assigned
            </span>
          );
        }
        return <span style={{ fontWeight: '500' }}>{value.name || value}</span>;
      }
    },
    {
      key: 'active_tickets',
      label: 'Active Tickets',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '700',
          background: 'var(--surface-light)',
          color: 'var(--text-primary)'
        }}>
          {value || 0}
        </span>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading SPOCs...
      </div>
    );
  }

  const deleteMessage = selectedSpoc?.active_tickets > 0
    ? `Warning: SPOC ${selectedSpoc?.name} currently has ${selectedSpoc.active_tickets} active ticket(s). Are you sure you want to delete them?`
    : `Are you sure you want to delete SPOC ${selectedSpoc?.name}? This action cannot be undone.`;

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
          Manage SPOCs
        </h2>
        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-secondary)' }}>
          Total SPOCs: <span style={{ color: 'var(--primary)' }}>{spocs.length}</span>
        </div>
      </div>

      <div style={filterBarStyle}>
        <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Input
            placeholder="Search SPOCs by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" onClick={loadSpocs}>
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Add SPOC
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredSpocs}
        actions={[
          { label: ' Edit', onClick: handleEditSpoc },
          { label: ' Delete', onClick: handleDeleteSpoc }
        ]}
        pageSize={15}
      />

      {/* Modals */}
      <AddSpocModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSpoc}
      />

      <EditSpocModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSpoc(null);
        }}
        onSubmit={handleUpdateSpoc}
        spoc={selectedSpoc}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedSpoc(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete SPOC"
        message={deleteMessage}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default SpocsView;